'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AddIcon,
  EditIcon,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  RemoveIcon,
} from '@/shared/ui/display/EmojiIcon';
import type { AttrDef, ConditionRule, DisplayTemplateRule } from '../model/moving-item.types';
import { ruleToText } from '../lib/conditionToText';
import { normalizeRuleForType, sanitizeConditionRule } from '../model/sanitizeConditionRule';
import { SentenceRuleEditor } from './SentenceRuleEditor';

function resolveRowKey(
  row: DisplayTemplateRule,
  index: number,
  counts: Map<string, number>
): string {
  const u = row._clientRowKey;
  if (u && (counts.get(u) ?? 0) === 1) return u;
  return `dt-${index}`;
}

export interface DisplayTemplateRuleListProps {
  attributes: AttrDef[];
  rules: DisplayTemplateRule[];
  onAddRule: () => void;
  onRemoveRule: (index: number) => void;
  onMoveRule: (index: number, delta: -1 | 1) => void;
  onUpdateRule: (index: number, rule: ConditionRule) => void;
  onUpdateTemplate: (index: number, template: string) => void;
  onClearRules?: () => void;
  /** 클라이언트 검증 실패 시 강조할 행 인덱스 */
  errorRowIndex?: number | null;
}

function useOwnerAttrForDisplayRules(attributes: AttrDef[]) {
  return useMemo(() => {
    const idx = attributes.findIndex((a) => a.key.trim());
    const ownerIndex = idx >= 0 ? idx : 0;
    const ownerAttrDef = attributes[ownerIndex] ?? {
      key: '',
      name: '',
      type: 'STRING' as const,
    };
    return { ownerIndex, ownerAttrDef };
  }, [attributes]);
}

function DisplayTemplateRuleRow({
  row,
  index,
  total,
  attributes,
  ownerIndex,
  ownerAttrDef,
  isLastRow,
  onUpdateRule,
  onUpdateTemplate,
  onRemove,
  onMoveUp,
  onMoveDown,
  highlightError,
  defaultEditing,
}: {
  row: DisplayTemplateRule;
  index: number;
  total: number;
  attributes: AttrDef[];
  ownerIndex: number;
  ownerAttrDef: AttrDef;
  isLastRow: boolean;
  onUpdateRule: (nextRule: ConditionRule) => void;
  onUpdateTemplate: (template: string) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  highlightError?: boolean;
  defaultEditing?: boolean;
}) {
  const { rule, template } = row;

  /** 표시명 규칙: 단일 절 CROSS_AND면 그 속성 기준, 아니면 스펙의 기본(첫 키) 속성 */
  const evalContext = useMemo(() => {
    if (rule.type === 'CROSS_AND' && rule.clauses?.length === 1) {
      const key = rule.clauses[0].key;
      const idx = attributes.findIndex((a) => a.key === key && a.key.trim());
      const def = idx >= 0 ? attributes[idx] : ownerAttrDef;
      const attrValues = def.type === 'ENUM' ? (def.values?.filter(Boolean) ?? []) : [];
      return { evalIndex: idx >= 0 ? idx : ownerIndex, evalDef: def, attrValues };
    }
    const attrValues =
      ownerAttrDef.type === 'ENUM' ? (ownerAttrDef.values?.filter(Boolean) ?? []) : [];
    return { evalIndex: ownerIndex, evalDef: ownerAttrDef, attrValues };
  }, [rule, attributes, ownerIndex, ownerAttrDef]);

  const handleMoveEvalAttr = useCallback(
    (toAttrIndex: number) => {
      const newDef = attributes[toAttrIndex];
      if (!newDef?.key.trim()) return;

      let inner: ConditionRule;
      let oldKey: string | undefined;

      if (rule.type === 'CROSS_AND' && rule.clauses?.length === 1) {
        oldKey = rule.clauses[0].key;
        inner = sanitizeConditionRule(rule.clauses[0].rule);
      } else {
        inner = sanitizeConditionRule(rule);
        oldKey = ownerAttrDef.key.trim() ? ownerAttrDef.key : undefined;
      }

      const oldDef = oldKey ? attributes.find((a) => a.key === oldKey) : undefined;
      let nextInner: ConditionRule = inner;
      if (!oldDef || oldDef.type !== newDef.type) {
        nextInner = { type: 'ALWAYS_MATCH' };
      } else if (newDef.type === 'ENUM' && inner.type === 'ENUM_IN') {
        const allowed = new Set((newDef.values ?? []).filter(Boolean));
        const vals = (inner.values ?? []).filter((v) => allowed.has(v));
        nextInner = { type: 'ENUM_IN', values: vals };
      }

      onUpdateRule({
        type: 'CROSS_AND',
        clauses: [{ key: newDef.key, rule: normalizeRuleForType(nextInner) }],
      });
    },
    [attributes, onUpdateRule, ownerAttrDef, rule]
  );

  const [editing, setEditing] = useState(defaultEditing ?? false);
  const snapshotRef = useRef<{ rule: ConditionRule; template: string } | null>(null);

  useEffect(() => {
    if (defaultEditing) setEditing(true);
  }, [defaultEditing]);

  const ruleChip = ruleToText(rule, evalContext.evalDef, attributes);

  const handleStartEdit = () => {
    snapshotRef.current = {
      rule: JSON.parse(JSON.stringify(rule)) as ConditionRule,
      template,
    };
    setEditing(true);
  };

  const handleCancel = () => {
    const snap = snapshotRef.current;
    if (snap) {
      onUpdateRule(snap.rule);
      onUpdateTemplate(snap.template);
    }
    snapshotRef.current = null;
    setEditing(false);
  };

  const handleDone = () => {
    snapshotRef.current = null;
    setEditing(false);
  };

  const showRuleEditor = !isLastRow || rule.type !== 'ALWAYS_MATCH';

  if (!editing) {
    return (
      <Box
        sx={{
          p: 1,
          border: 1,
          borderColor: highlightError ? 'error.main' : 'divider',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Chip label={`IF: ${ruleChip}`} size="small" variant="outlined" sx={{ maxWidth: '100%' }} />
        <Typography variant="body2" color="text.secondary">
          →
        </Typography>
        <Chip
          label={
            template.trim()
              ? template.slice(0, 48) + (template.length > 48 ? '…' : '')
              : '(템플릿 없음)'
          }
          size="small"
          color="primary"
          variant="outlined"
          sx={{ maxWidth: '100%' }}
        />
        {isLastRow && (
          <Chip
            label="기본(마지막)"
            size="small"
            variant="filled"
            color="default"
            sx={{ ml: 0.5 }}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={onMoveUp} disabled={index <= 0} aria-label="위로">
          <KeyboardArrowUpIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onMoveDown}
          disabled={index >= total - 1}
          aria-label="아래로"
        >
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleStartEdit} aria-label="규칙 편집">
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onRemove} aria-label="규칙 삭제">
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 1,
        border: 1,
        borderColor: highlightError ? 'error.main' : 'primary.light',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {isLastRow && rule.type === 'ALWAYS_MATCH' ? (
        <Typography variant="body2" color="text.secondary">
          조건: <strong>항상 참</strong> — 위 규칙에 맞지 않을 때 이 템플릿이 쓰입니다.
        </Typography>
      ) : (
        showRuleEditor && (
          <SentenceRuleEditor
            rule={rule}
            attrType={evalContext.evalDef.type}
            attrValues={evalContext.attrValues}
            allAttrDefs={attributes}
            ownerAttrIndex={evalContext.evalIndex}
            ownerAttrLabel={evalContext.evalDef.name?.trim() || evalContext.evalDef.key || '(속성)'}
            onChange={onUpdateRule}
            onMoveEvalAttr={handleMoveEvalAttr}
          />
        )
      )}

      <Typography variant="subtitle2" color="primary">
        표시 템플릿
      </Typography>
      <TextField
        variant="filled"
        size="small"
        label="템플릿 문자열"
        multiline
        minRows={2}
        fullWidth
        value={template}
        onChange={(e) => onUpdateTemplate(e.target.value)}
        placeholder="{displayName} {attr:inch:인치}"
        helperText="{displayName}, {attr:키}, {attr:키:접미사}"
      />

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={onMoveUp} disabled={index <= 0} aria-label="위로">
          <KeyboardArrowUpIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onMoveDown}
          disabled={index >= total - 1}
          aria-label="아래로"
        >
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
        <Button size="small" onClick={handleCancel}>
          취소
        </Button>
        <Button size="small" variant="contained" onClick={handleDone}>
          완료
        </Button>
      </Box>
    </Box>
  );
}

export function DisplayTemplateRuleList({
  attributes,
  rules,
  onAddRule,
  onRemoveRule,
  onMoveRule,
  onUpdateRule,
  onUpdateTemplate,
  onClearRules,
  errorRowIndex,
}: DisplayTemplateRuleListProps) {
  const theme = useTheme();
  const { ownerIndex, ownerAttrDef } = useOwnerAttrForDisplayRules(attributes);

  const keyCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rules) {
      const u = r._clientRowKey;
      if (u) m.set(u, (m.get(u) ?? 0) + 1);
    }
    return m;
  }, [rules]);

  const [openKey, setOpenKey] = useState<string | null>(null);

  const handleAdd = () => {
    onAddRule();
    setOpenKey('pending');
  };

  useEffect(() => {
    if (openKey !== 'pending' || rules.length === 0) return;
    const last = rules[rules.length - 1];
    setOpenKey(resolveRowKey(last, rules.length - 1, keyCounts));
  }, [openKey, rules, keyCounts]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Alert
        severity="info"
        variant="outlined"
        sx={{
          alignItems: 'flex-start',
          py: 0.75,
          px: 1,
          bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
          borderColor: alpha(theme.palette.info.main, 0.28),
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          표시명 규칙 안내
        </Typography>
        <Typography variant="body2" component="div">
          위에서부터 첫 번째로 맞는 규칙의 템플릿이 적용됩니다. 조건부 규칙을 쓰는 경우 마지막 줄은
          반드시 &quot;항상 참&quot;이어야 합니다. 단일 속성 조건(숫자·불리언·옵션 비교)은 그 위
          행에 둘 수 있으며, 여러 속성을 함께 묶을 때는 CROSS_AND·AND를 사용하세요.
        </Typography>
      </Alert>

      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          조건부 표시명이 없습니다. 기본 템플릿만 쓰거나 아래에서 규칙을 추가하세요.
        </Typography>
      ) : (
        rules.map((row, index) => (
          <DisplayTemplateRuleRow
            key={resolveRowKey(row, index, keyCounts)}
            row={row}
            index={index}
            total={rules.length}
            attributes={attributes}
            ownerIndex={ownerIndex}
            ownerAttrDef={ownerAttrDef}
            isLastRow={index === rules.length - 1}
            onUpdateRule={(updates) => onUpdateRule(index, updates)}
            onUpdateTemplate={(t) => onUpdateTemplate(index, t)}
            onRemove={() => onRemoveRule(index)}
            onMoveUp={() => onMoveRule(index, -1)}
            onMoveDown={() => onMoveRule(index, 1)}
            highlightError={errorRowIndex === index}
            defaultEditing={openKey === resolveRowKey(row, index, keyCounts)}
          />
        ))
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>
          조건부 규칙 추가
        </Button>
        {onClearRules && rules.length > 0 && (
          <Button size="small" variant="text" color="warning" onClick={onClearRules}>
            조건부 규칙 전부 비우기
          </Button>
        )}
      </Box>
    </Box>
  );
}
